import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  makeStyles,
} from '@material-ui/core';
import DateRangeIcon from '@material-ui/icons/DateRange';
import { DatePicker, MuiPickersContext } from '@material-ui/pickers';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import AdornmentTextInput from './AdornmentTextInput';
import DateInput from './DateInput';

const useStyles = makeStyles((theme) => ({
  inputText: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#707070',
      },
    },
  },
  day: {
    width: 36,
    height: 36,
    fontSize: theme.typography.caption.fontSize,
    margin: '0 2px',
    color: 'inherit',
  },
  customDayHighlight: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: '2px',
    right: '2px',
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: '50%',
  },
  nonCurrentMonthDay: {
    opacity: 0,
    pointerEvents: 'none',
  },
  highlight: {
    background: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  firstHighlight: {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
  },
  endHighlight: {
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
  },
  dialogPaper: {
    maxWidth: 310 + theme.spacing(6),
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
  },
  dialogContent: {
    '&.MuiDialogContent-root': {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  menu: {
    height: 300,
  },
  buttonOk: {
    color: '#43a047',
    borderColor: '#43a047',
  },
  buttonCancel: {
    color: '#f44336',
  },
  containerDateInputs: {
    display: 'flex',
    flexDirection: 'row',
    padding: 8,
    '&>:first-child': {
      marginRight: 8,
    },
  },
}));

const RangeDateInputPicker = ({ value, onChange }) => {
  const [begin, setBegin] = useState(value[0]);
  const [end, setEnd] = useState(value[1]);
  const [hover, setHover] = useState(null);
  const utils = useContext(MuiPickersContext);

  const classes = useStyles();

  const min = Math.min(begin, end || hover);
  const max = Math.max(begin, end || hover);

  function renderDay(day, selectedDate, dayInCurrentMonth, dayComponent) {
    const style = {
      margin: 0,
      width: '40px',
    };

    if (begin && day >= min && day <= max) {
      style.backgroundColor = '#3f51b5';
      style.color = 'white';
    }

    if (utils.isSameDay(day, min)) style.borderRadius = '50% 0 0 50%';
    else if (utils.isSameDay(day, max)) style.borderRadius = '0 50% 50% 0';
    else style.borderRadius = '0';

    return React.cloneElement(dayComponent, {
      onClick: (e) => {
        e.stopPropagation();

        if (!begin) setBegin(day);
        else if (!end) {
          setEnd(day);
          onChange(begin, day);
        } else {
          setBegin(day);
          setEnd(null);
        }
      },
      onMouseEnter: () => setHover(day),
      style,
    });
  }

  const handleChangeBegin = ({ target: { value } }) => {
    if (value && end && end.isValid()) {
      onChange(value, end);
    }

    setBegin(value);
  };
  const handleChangeEnd = ({ target: { value } }) => {
    if (begin && begin.isValid() && value) {
      onChange(begin, value);
    }
    setEnd(value);
  };

  return (
    <>
      <div className={classes.containerDateInputs}>
        <DateInput
          id="rangeDateInput.dataInicial"
          name="rangeDateInput.dataInicial"
          className="mr-8"
          onChange={handleChangeBegin}
          value={begin}
          error={undefined}
          helperText={undefined}
          touched={!!undefined}
          autofocus
          required
          fullWidth
          variant="outlined"
          label="Data inicial"
        />
        <DateInput
          id="rangeDateInput.dataFinal"
          name="rangeDateInput.dataFinal"
          onChange={handleChangeEnd}
          value={end}
          error={undefined}
          helperText={undefined}
          touched={!!undefined}
          required
          fullWidth
          variant="outlined"
          label="Data final"
        />
      </div>
      <DatePicker value={null} renderDay={renderDay} variant="static" clearable onChange={() => {}} disableToolbar />
    </>
  );
};

const RangeDateInput = ({
  id,
  name,
  onChange,
  onBlur,
  value,
  label,
  required,
  error,
  helperText,
  touched,
  dialogTitle,
  disabled,
}) => {
  const classes = useStyles();

  const [open, setOpen] = useState(false);

  const [begin, setBegin] = useState(undefined);
  const [end, setEnd] = useState(undefined);

  const renderValue = (begin, end) => {
    if (begin && end) {
      return `${begin.format('DD/MM/YYYY')} a ${end.format('DD/MM/YYYY')}`;
    }
    return ' ';
  };

  const handleConfirm = () => {
    if (begin && end) {
      const min = begin.isAfter(end) ? end : begin;
      const max = end.isBefore(begin) ? begin : end;

      onChange(min, max);
    }

    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleDateChange = (_begin, _end) => {
    setBegin(_begin);
    setEnd(_end);
  };

  const handleOpen = (e) => {
    if (disabled) return;

    if (!(e?.key === 'Tab' || e?.key === 'Esc')) {
      setOpen(true);
    }
  };

  const handleClear = () => {
    onChange(undefined, undefined);
    setOpen(false);
  };

  return (
    <>
      <AdornmentTextInput
        id={id}
        name={name}
        onChange={() => {}}
        onKeyDown={handleOpen}
        value={renderValue(value[0], value[1])}
        onClick={handleOpen}
        touched={touched}
        onBlur={onBlur}
        label={label}
        error={error}
        helperText={helperText}
        required={required}
        className={classes.inputText}
        fullWidth
        endAdornment={
          <InputAdornment position="end">
            <IconButton aria-label="calendar" edge="end" disabled={disabled}>
              <DateRangeIcon />
            </IconButton>
          </InputAdornment>
        }
        disabled={disabled}
      />
      <Dialog
        aria-labelledby="dateRangePicker-dialog-title"
        open={open}
        onClose={handleCancel}
        classes={{
          paper: classes.dialogPaper,
        }}
      >
        <DialogTitle id="dateRangePicker-dialog-title" style={{ paddingBottom: 0 }}>
          {dialogTitle}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <RangeDateInputPicker value={value} onChange={handleDateChange} />
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button className={classes.buttonClear} onClick={handleClear}>
            Limpar
          </Button>
          <div style={{ flex: 1 }} />
          <Button className={classes.buttonCancel} onClick={handleCancel}>
            Cancelar
          </Button>
          <Button className={classes.buttonOk} onClick={handleConfirm} variant="outlined" disabled={!begin || !end}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

RangeDateInput.defaultProps = {
  onClose: () => {},
  variant: 'outlined',
  fullWidth: true,
  label: '',
  dialogTitle: 'Alterar período',
};

RangeDateInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.arrayOf(PropTypes.object),
  label: PropTypes.string,
  variant: PropTypes.string,
  error: PropTypes.string,
  touched: PropTypes.bool,
  dialogTitle: PropTypes.string,
};

export default RangeDateInput;
